import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchImageAsBlob } from '../services/api';
import useDialogFocus from '../hooks/useDialogFocus';
import { RENDER_MAX_SIDE, renderSize } from './ImageAnnotator';
import {
  FORMATS, FULL_FRAME, fitFormat, fractionRatio, framePixels, isFullFrame,
  moveFrame, parseSavedCrop, resizeFrame, rotatedSize,
} from '../utils/cropGeometry';

const RENDER_QUALITY = 0.9;
const STAGE_PADDING = 28; // room around the photo for the corner handles
const KEY_STEP = 0.01;
const KEY_STEP_FAST = 0.05;
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CORNERS = ['nw', 'ne', 'se', 'sw'];

/** The original photo turned by `rotation` degrees, drawn on a canvas. */
function rotateToCanvas(img, rotation) {
  const { width, height } = rotatedSize(img.naturalWidth, img.naturalHeight, rotation);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.translate(width / 2, height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return canvas;
}

function RotateIcon({ clockwise }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"
      style={clockwise ? { transform: 'scaleX(-1)' } : undefined}>
      <path d="M7.5 7.5H4V4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.6 7.4A8 8 0 1 1 4 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Full-screen photo cropper: a frame with large handles over the original,
 * three formats, quarter turns. The original is never changed: the result is
 * a separate render, and the frame is kept so the photo can be re-cropped.
 */
export default function ImageCropper({
  reportId, filename, savedCrop, hasCrop, hasAnnotations, title, onApply, onReset, onCancel,
}) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const stageRef = useRef(null);
  const sourceRef = useRef(null); // the original <img>
  const dragRef = useRef(null);
  const initial = useRef(parseSavedCrop(savedCrop)).current;

  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [rotation, setRotation] = useState(initial.rotation);
  const [frame, setFrame] = useState(initial.frame);
  const [format, setFormat] = useState('free');
  const [view, setView] = useState(null); // { url, width, height } of the rotated photo
  const [box, setBox] = useState(null); // displayed size of the photo, in px
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useDialogFocus(dialogRef, { onEscape: () => { if (!saving) onCancel(); } });

  // The page underneath must not scroll behind the overlay.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Load the original photo once.
  useEffect(() => {
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        url = await fetchImageAsBlob(reportId, filename);
        const img = new Image();
        img.src = url;
        await img.decode();
        if (cancelled) return;
        sourceRef.current = img;
        setLoaded(true);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [reportId, filename]);

  // The photo as shown: turned on a canvas whenever the rotation changes.
  useEffect(() => {
    const img = sourceRef.current;
    if (!loaded || !img) return undefined;
    if (rotation === 0) {
      setView({ url: img.src, width: img.naturalWidth, height: img.naturalHeight });
      return undefined;
    }
    const canvas = rotateToCanvas(img, rotation);
    let url = null;
    let cancelled = false;
    canvas.toBlob((blob) => {
      if (cancelled || !blob) return;
      url = URL.createObjectURL(blob);
      setView({ url, width: canvas.width, height: canvas.height });
    }, 'image/jpeg', 0.85);
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [loaded, rotation]);

  // Fit the photo in the stage, and again when the tablet is turned.
  const fit = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || !view) return;
    const availW = Math.max(1, stage.clientWidth - STAGE_PADDING * 2);
    const availH = Math.max(1, stage.clientHeight - STAGE_PADDING * 2);
    const scale = Math.min(availW / view.width, availH / view.height);
    setBox({ width: Math.round(view.width * scale), height: Math.round(view.height * scale) });
  }, [view]);

  useLayoutEffect(() => {
    fit();
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [fit]);

  const ratioFor = (key, size = view) => {
    const chosen = FORMATS.find((f) => f.key === key);
    return chosen && size ? fractionRatio(chosen.ratio, size.width, size.height) : null;
  };

  const chooseFormat = (key) => {
    setFormat(key);
    setFrame((current) => fitFormat(current, ratioFor(key)));
  };

  const rotate = (delta) => {
    const next = (rotation + delta + 360) % 360;
    setRotation(next);
    // A new orientation starts from the whole photo, in the chosen format.
    const img = sourceRef.current;
    const size = img ? rotatedSize(img.naturalWidth, img.naturalHeight, next) : null;
    setFrame(fitFormat(FULL_FRAME, ratioFor(format, size)));
  };

  const wholePhoto = () => {
    setFormat('free');
    setRotation(0);
    setFrame(FULL_FRAME);
  };

  // --- Dragging: the frame itself moves it, a handle resizes it ---
  const startDrag = (e, handle) => {
    if (!box || saving) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { handle, x: e.clientX, y: e.clientY, frame };
    setDragging(true);
  };

  const onDrag = (e) => {
    const drag = dragRef.current;
    if (!drag || !box) return;
    const dx = (e.clientX - drag.x) / box.width;
    const dy = (e.clientY - drag.y) / box.height;
    setFrame(drag.handle === 'move'
      ? moveFrame(drag.frame, dx, dy)
      : resizeFrame(drag.frame, drag.handle, dx, dy, ratioFor(format)));
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  // Arrow keys move the frame; with Shift, faster.
  const onFrameKey = (e) => {
    const step = e.shiftKey ? KEY_STEP_FAST : KEY_STEP;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    setFrame((current) => moveFrame(current, move[0], move[1]));
  };

  const whole = isFullFrame(frame, rotation);

  const apply = async () => {
    const img = sourceRef.current;
    if (!img || saving) return;
    // Nothing to crop: back to the original if it was cropped, else just close.
    if (whole) {
      if (!hasCrop) { onCancel(); return; }
      setSaving(true);
      setError(null);
      try {
        await onReset();
      } catch {
        setError(t('crop.saveFailed'));
        setSaving(false);
      }
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const turned = rotation === 0 ? img : rotateToCanvas(img, rotation);
      const fullW = rotation === 0 ? img.naturalWidth : turned.width;
      const fullH = rotation === 0 ? img.naturalHeight : turned.height;
      const { sx, sy, sw, sh } = framePixels(frame, fullW, fullH);
      const { width, height } = renderSize(sw, sh, RENDER_MAX_SIDE);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(turned, sx, sy, sw, sh, 0, 0, width, height);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('empty render'))), 'image/jpeg', RENDER_QUALITY);
      });
      await onApply(blob, { rotation, ...frame });
    } catch {
      setError(t('crop.saveFailed'));
      setSaving(false);
    }
  };

  const ratioLocked = Boolean(ratioFor(format));
  const handles = ratioLocked ? CORNERS : HANDLES;
  const pct = (v) => `${v * 100}%`;

  return (
    <div className="cropper" role="dialog" aria-modal="true" aria-labelledby="cropper-title"
      ref={dialogRef} tabIndex={-1}>
      <header className="cropper-bar">
        <button type="button" className="cropper-text-btn" onClick={onCancel} disabled={saving}>
          {t('crop.cancel')}
        </button>
        <h2 id="cropper-title" className="cropper-title">{title}</h2>
        <button type="button" className="cropper-apply" onClick={apply} disabled={!view || saving}>
          {saving ? t('crop.saving') : t('crop.apply')}
        </button>
      </header>

      <div className="cropper-stage" ref={stageRef}>
        {loadError && <p className="cropper-message" role="alert">{t('crop.loadFailed')}</p>}
        {!loadError && !(view && box) && <p className="cropper-message">{t('crop.loading')}</p>}
        {view && box && (
          <div className="cropper-photo" style={{ width: box.width, height: box.height }}>
            <img src={view.url} alt="" draggable={false} />
            <div
              className={`cropper-frame${dragging ? ' is-dragging' : ''}`}
              style={{ left: pct(frame.x), top: pct(frame.y), width: pct(frame.w), height: pct(frame.h) }}
              role="group"
              tabIndex={0}
              aria-label={t('crop.frameLabel')}
              onPointerDown={(e) => startDrag(e, 'move')}
              onPointerMove={onDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onKeyDown={onFrameKey}
            >
              <span className="cropper-grid" aria-hidden="true" />
              {handles.map((h) => (
                <span
                  key={h}
                  className={`cropper-handle cropper-handle-${h}`}
                  aria-hidden="true"
                  onPointerDown={(e) => startDrag(e, h)}
                  onPointerMove={onDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="cropper-tools">
        {hasAnnotations && (
          <p className="cropper-note cropper-note-warn">{t('crop.annotationsWarning')}</p>
        )}
        {error && <p className="cropper-note cropper-note-error" role="alert">{error}</p>}
        <div className="cropper-tool-row">
          <div className="cropper-formats" role="radiogroup" aria-label={t('crop.format')}>
            {FORMATS.map((f) => (
              <button
                key={f.key}
                type="button"
                role="radio"
                aria-checked={format === f.key}
                className={`cropper-chip${format === f.key ? ' is-on' : ''}`}
                onClick={() => chooseFormat(f.key)}
                disabled={!view || saving}
              >
                {t(`crop.format_${f.key}`)}
              </button>
            ))}
          </div>
          <div className="cropper-actions">
            <button type="button" className="cropper-icon-btn" onClick={() => rotate(-90)}
              disabled={!view || saving} aria-label={t('crop.rotateLeft')} title={t('crop.rotateLeft')}>
              <RotateIcon />
            </button>
            <button type="button" className="cropper-icon-btn" onClick={() => rotate(90)}
              disabled={!view || saving} aria-label={t('crop.rotateRight')} title={t('crop.rotateRight')}>
              <RotateIcon clockwise />
            </button>
            <button type="button" className="cropper-text-btn" onClick={wholePhoto}
              disabled={!view || saving || whole}>
              {t('crop.wholePhoto')}
            </button>
          </div>
        </div>
        <p className="cropper-note">{t('crop.originalKept')}</p>
      </footer>
    </div>
  );
}
